import { useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  Briefcase,
  ChevronRight,
  FileText,
  Gauge,
  MessageCirclePlus,
  MessageSquare,
  RotateCw,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { aiService } from "@/api/ai";
import { chatThreadsService, type TTopThread } from "@/api/chat";
import { CompanyRole } from "@/api/companies";
import { dealNetworkService } from "@/api/dealNetwork";
import { filesService, KNOWLEDGE_BASE_FILE_TYPE_PARAM } from "@/api/files";
import { CompanyKnowledgeList } from "@/components/company-knowledge/CompanyKnowledgeList";
import { CompanyKnowledgeUploadCard } from "@/components/company-knowledge/CompanyKnowledgeUploadCard";
import { AppCard } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  DEFAULT_SECTION_2,
  DEFAULT_SECTION_4,
  DEFAULT_SECTION_5,
  DEFAULT_SECTION_6,
  getSectionDescription,
} from "@/constants/sections";
import { useStore } from "@/store";
import { useChatStore } from "@/store/chat/chat.store";

type TGeneralDashboardFeatureProps = {
  sectionId: string | null;
  sectionName: string;
  description?: string | null;
  companyId?: string | null;
  companyName?: string | null;
};

const GENERAL_DASHBOARD_FALLBACK =
  "Your central hub for fundraising and investment activity. Access your chats, manage company knowledge, and stay on top of your deal pipeline at a glance.";

const RECENT_THREADS_LIMIT = 5;

/** Best-effort extraction of the readiness average score from the loosely-typed payload. */
function extractReadinessScore(readinessData: Record<string, unknown> | null): number | null {
  if (!readinessData) return null;
  const companyReadiness = readinessData.company_readiness;
  if (companyReadiness && typeof companyReadiness === "object") {
    const summary = (companyReadiness as Record<string, unknown>).summary;
    if (summary && typeof summary === "object") {
      const averageScore = (summary as Record<string, unknown>).average_score;
      if (typeof averageScore === "number") return Math.round(averageScore);
    }
  }
  return null;
}

function formatRelativeTime(dateString: string): string {
  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffMinutes = Math.floor(diffMs / 60_000);
  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(dateString).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

type TStatTileProps = {
  icon: typeof Gauge;
  label: string;
  /** Omit for a navigation-only tile — a metric with no real source should show a route, not a dash. */
  value?: string | null;
  hint: string;
  /** Sub-label for navigation-only tiles, where there is no number to carry the meaning. */
  caption?: string;
  isLoading?: boolean;
  onClick?: () => void;
};

function StatTile({ icon: Icon, label, value, hint, caption, isLoading = false, onClick }: TStatTileProps) {
  const isMetric = value !== undefined;

  const body = (
    <>
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
      </div>
      <div className="mt-3 flex items-end justify-between gap-2">
        {isMetric ? (
          isLoading ? (
            <Skeleton className="h-8 w-16" />
          ) : (
            <p className="text-2xl font-semibold leading-none text-foreground">{value ?? "—"}</p>
          )
        ) : (
          <p className="max-w-[70%] text-sm leading-snug text-foreground">{caption}</p>
        )}
        {onClick ? (
          <span className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-semibold text-primary">
            {hint}
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{hint}</span>
        )}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={`${label}: ${isMetric ? (value ?? "not available yet") : caption} — ${hint}`}
        className="rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-colors hover:border-primary/40 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {body}
      </button>
    );
  }
  return <div className="rounded-xl border border-border bg-card p-4 shadow-sm">{body}</div>;
}

export function GeneralDashboardFeature({
  sectionId,
  sectionName,
  description,
  companyId,
  companyName,
}: TGeneralDashboardFeatureProps) {
  const navigate = useNavigate();
  const [topThreads, setTopThreads] = useState<TTopThread[]>([]);
  const [isLoadingTopThreads, setIsLoadingTopThreads] = useState(true);
  const [topThreadsError, setTopThreadsError] = useState<string | null>(null);
  const [readinessScore, setReadinessScore] = useState<number | null>(null);
  const [isLoadingReadiness, setIsLoadingReadiness] = useState(true);
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [isLoadingMatches, setIsLoadingMatches] = useState(true);
  const [documentCount, setDocumentCount] = useState<number | null>(null);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);

  const { openChatThreadSession, closeDashboard } = useChatStore(
    useShallow((state) => ({
      openChatThreadSession: state.openChatThreadSession,
      closeDashboard: state.closeDashboard,
    }))
  );

  const getCompanyById = useStore((state) => state.getCompanyById);

  // The General hub is the one dashboard both roles land on, so its tiles, links and copy
  // follow the company's role. Founders care about readiness and investor matches;
  // investors care about opportunities and diligence.
  const company = companyId ? getCompanyById(companyId) : null;
  const isInvestor = company?.role === CompanyRole.INVESTOR;

  const [knowledgeRefetchTrigger, setKnowledgeRefetchTrigger] = useState(0);
  const handleKnowledgeUploadSuccess = useCallback(() => {
    setKnowledgeRefetchTrigger((n) => n + 1);
    // Refresh the documents stat alongside the list.
    void filesService
      .listFiles({ company_id: companyId ?? "", file_type: KNOWLEDGE_BASE_FILE_TYPE_PARAM })
      .then(({ data }) => {
        const files = data?.data?.files;
        if (Array.isArray(files)) setDocumentCount(files.length);
      });
  }, [companyId]);

  const sectionLabel = sectionName.trim().length > 0 ? sectionName : "General Dashboard";
  const descriptionText =
    (description?.trim().length ?? 0) > 0
      ? description
      : getSectionDescription(sectionId) || GENERAL_DASHBOARD_FALLBACK;

  const fetchTopThreads = useCallback(async () => {
    setIsLoadingTopThreads(true);
    setTopThreadsError(null);

    try {
      const { data, error } = await chatThreadsService.getTopThreads(RECENT_THREADS_LIMIT, {
        companyId: companyId ?? undefined,
      });

      if (error) {
        setTopThreadsError("Failed to load recent chats");
        setTopThreads([]);
        return;
      }

      const threads = data?.data?.threads ?? [];
      setTopThreads(threads);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error("[general-dashboard] Failed to fetch top threads", { error: err });
      }
      setTopThreadsError("Failed to load recent chats");
      setTopThreads([]);
    } finally {
      setIsLoadingTopThreads(false);
    }
  }, [companyId]);

  useEffect(() => {
    void fetchTopThreads();
  }, [fetchTopThreads]);

  // Overview stats: each fetch is independent and failure-tolerant — a broken
  // stat renders as "—" rather than blocking the page.
  useEffect(() => {
    if (!companyId) {
      setIsLoadingReadiness(false);
      setIsLoadingMatches(false);
      setIsLoadingDocuments(false);
      return;
    }

    let isCancelled = false;

    // Readiness scoring is a founder concept — don't spend the request for investors.
    if (isInvestor) {
      setIsLoadingReadiness(false);
    } else {
      void aiService.getCompanyReadiness(companyId).then(({ data }) => {
        if (isCancelled) return;
        setReadinessScore(extractReadinessScore(data?.data?.readiness_data ?? null));
        setIsLoadingReadiness(false);
      });
    }

    void dealNetworkService.getCompanyRecommendations(companyId, { timeout: 30_000 }).then(({ data }) => {
      if (isCancelled) return;
      const recommendations = data?.data ?? [];
      setMatchCount(recommendations.filter((rec) => !rec.dismissed).length);
      setIsLoadingMatches(false);
    });

    void filesService.listFiles({ company_id: companyId, file_type: KNOWLEDGE_BASE_FILE_TYPE_PARAM }).then(({ data }) => {
      if (isCancelled) return;
      const files = data?.data?.files;
      setDocumentCount(Array.isArray(files) ? files.length : 0);
      setIsLoadingDocuments(false);
    });

    return () => {
      isCancelled = true;
    };
  }, [companyId, isInvestor]);

  const handleNewChat = useCallback(() => {
    if (!companyId || !sectionId) return;
    closeDashboard();
    void navigate({
      to: "/app/companies/$company_id/sections/$section_id/new",
      params: { company_id: companyId, section_id: sectionId },
    });
  }, [companyId, sectionId, closeDashboard, navigate]);

  const openSectionDashboard = useCallback(
    (targetSectionId: string) => {
      if (!companyId) return;
      void navigate({
        to: "/app/companies/$company_id/sections/$section_id/dashboard",
        params: { company_id: companyId, section_id: targetSectionId },
      });
    },
    [companyId, navigate]
  );

  const handleThreadClick = (thread: TTopThread) => {
    const company = thread.company_id ? getCompanyById(thread.company_id) : null;
    const companyNameForThread = company?.name ?? companyName ?? undefined;

    // Close dashboard if open
    closeDashboard();

    // Open chat thread session
    openChatThreadSession({
      threadId: thread.id,
      companyId: thread.company_id,
      companyName: companyNameForThread,
      sectionId: thread.section_id,
      sectionName: sectionName,
      title: thread.title ?? "Chat Thread",
    });

    // Navigate to the thread route
    void navigate({
      to: "/app/companies/$company_id/sections/$section_id/threads/$thread_id",
      params: {
        company_id: thread.company_id,
        section_id: thread.section_id,
        thread_id: thread.id,
      },
    });
  };

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Header: section context + primary action */}
      <AppCard className="flex-shrink-0 p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-semibold leading-tight text-foreground sm:text-lg">{sectionLabel}</h2>
            {companyName ? <p className="mt-0.5 text-sm font-medium text-muted-foreground">{companyName}</p> : null}
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">{descriptionText}</p>
          </div>
          <Button
            onClick={handleNewChat}
            className="flex-shrink-0 gap-2"
            disabled={!companyId || !sectionId}
          >
            <MessageCirclePlus className="h-4 w-4" aria-hidden="true" />
            New chat
          </Button>
        </div>
      </AppCard>

      {/* Overview stats — role-aware */}
      <div className="grid flex-shrink-0 grid-cols-1 gap-4 sm:grid-cols-3">
        {isInvestor ? (
          <StatTile
            icon={Briefcase}
            label="Deal pipeline"
            caption="Track sourcing, diligence and execution"
            hint="Open pipeline"
            onClick={() => openSectionDashboard(DEFAULT_SECTION_5)}
          />
        ) : (
          <StatTile
            icon={Gauge}
            label="Investment readiness"
            value={readinessScore != null ? `${readinessScore}/100` : null}
            hint="View analysis"
            isLoading={isLoadingReadiness}
            onClick={() => openSectionDashboard(DEFAULT_SECTION_2)}
          />
        )}
        <StatTile
          icon={Users}
          label={isInvestor ? "Matched opportunities" : "Investor matches"}
          value={matchCount != null ? String(matchCount) : null}
          hint={isInvestor ? "Browse deals" : "View matches"}
          isLoading={isLoadingMatches}
          onClick={() => openSectionDashboard(isInvestor ? DEFAULT_SECTION_6 : DEFAULT_SECTION_4)}
        />
        <StatTile
          icon={FileText}
          label="Knowledge documents"
          value={documentCount != null ? String(documentCount) : null}
          hint="Manage below"
          isLoading={isLoadingDocuments}
        />
      </div>

      {/* Two columns: left = Recent chats; right = Company Knowledge */}
      <div className="grid min-h-0 flex-1 content-start grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2 lg:items-stretch">
        {/* Left column: Recent chats */}
        <div className="flex min-h-0 flex-col">
          <AppCard className="p-4 sm:p-6">
            <div className="mb-3 flex items-center justify-between sm:mb-4">
              <h3 className="text-base font-semibold text-foreground sm:text-lg">Chats</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNewChat}
                disabled={!companyId || !sectionId}
                className="h-8 gap-1.5 px-2 text-xs text-primary hover:text-primary"
              >
                <MessageCirclePlus className="h-3.5 w-3.5" aria-hidden="true" />
                New chat
              </Button>
            </div>
            {isLoadingTopThreads ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg px-3 py-2.5">
                    <Skeleton className="h-4 w-4 flex-shrink-0 rounded" />
                    <Skeleton className="h-4 flex-1" />
                    <Skeleton className="h-3 w-12 flex-shrink-0" />
                  </div>
                ))}
              </div>
            ) : topThreadsError ? (
              <div className="flex flex-col items-start gap-2 py-2">
                <p className="text-sm text-destructive">{topThreadsError}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void fetchTopThreads()}
                  className="h-7 px-2 text-xs text-muted-foreground"
                >
                  <RotateCw className="mr-1 h-3 w-3" />
                  Try again
                </Button>
              </div>
            ) : topThreads.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-muted p-6 text-center">
                <MessageSquare className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden="true" />
                <p className="mt-2 text-sm text-muted-foreground">
                  {isInvestor
                    ? "No conversations yet. Start a chat to evaluate a deal or shape your thesis."
                    : "No conversations yet. Start a chat to get AI-powered fundraising guidance."}
                </p>
              </div>
            ) : (
              <ul className="space-y-1">
                {topThreads.map((thread) => (
                  <li key={thread.id}>
                    <button
                      type="button"
                      onClick={() => handleThreadClick(thread)}
                      className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-muted"
                    >
                      <MessageSquare className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate font-medium">{thread.title ?? "New Chat"}</span>
                      <span className="flex-shrink-0 text-xs text-muted-foreground">
                        {formatRelativeTime(thread.updated_at ?? thread.created_at)}
                      </span>
                      <ChevronRight
                        className="h-4 w-4 flex-shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </AppCard>
        </div>

        {/* Right column: Company Knowledge */}
        <div className="flex min-h-0 flex-col">
          <AppCard className="flex h-full flex-col p-4 sm:p-6">
            <h3 className="text-base font-semibold text-foreground sm:text-lg">Company Knowledge</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {isInvestor
                ? "Documents you upload here — your thesis, mandate or diligence notes — are used by Fundy's AI when it evaluates deals for you."
                : "Documents you upload here are shared with Fundy's AI to improve analysis quality."}
            </p>
            {/* TODO: restore capacity bar once backend provides real usage data */}
            {companyId && (
              <>
                <div className="mt-4 flex-shrink-0">
                  <CompanyKnowledgeUploadCard
                    companyId={companyId}
                    onUploadSuccess={handleKnowledgeUploadSuccess}
                  />
                </div>
                <div className="mt-4 min-h-0 flex-1 overflow-auto">
                  <CompanyKnowledgeList
                    companyId={companyId}
                    companyName={companyName}
                    maxItems={5}
                    showTitle={false}
                    refetchTrigger={knowledgeRefetchTrigger}
                  />
                </div>
              </>
            )}
          </AppCard>
        </div>
      </div>
    </div>
  );
}
